import React, { useState } from 'react';

const card = {
  background: 'rgba(30,45,79,0.5)',
  borderRadius: '16px',
  border: '1px solid rgba(0,194,168,0.1)',
  padding: '24px',
  marginBottom: '0',
};

function PassBadge({ rate }) {
  const color = rate >= 90 ? '#00C2A8' : rate >= 70 ? '#F5A623' : '#E8415A';
  return (
    <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
      <div style={{ flex:1, height:'6px', background:'#1E2D4F', borderRadius:'3px', overflow:'hidden', minWidth:'60px' }}>
        <div style={{ height:'100%', width:`${rate}%`, background:color, borderRadius:'3px', boxShadow:`0 0 6px ${color}60` }} />
      </div>
      <span style={{ fontFamily:'JetBrains Mono', fontSize:'12px', color, minWidth:'36px', textAlign:'right' }}>{rate}%</span>
    </div>
  );
}

function countryFlag(code) {
  if (!code || code.length !== 2) return '';
  return String.fromCodePoint(
    0x1F1E6 + code.toUpperCase().charCodeAt(0) - 65,
    0x1F1E6 + code.toUpperCase().charCodeAt(1) - 65
  );
}

export default function SourceTable({ sources, geo, compact }) {
  const g = geo || {};
  const [sortBy, setSortBy] = useState('total');

  const sorted = [...(sources || [])].sort((a, b) => {
    if (sortBy === 'rate') return (b.pass / b.total) - (a.pass / a.total);
    return b[sortBy] - a[sortBy];
  });

  const thStyle = (col) => ({
    padding: '8px 12px',
    textAlign: col === 'ip' ? 'left' : 'right',
    fontFamily: 'Space Grotesk',
    fontSize: '11px',
    color: sortBy === col ? '#00C2A8' : '#5B7BA8',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    borderBottom: '1px solid rgba(0,194,168,0.1)',
  });

  const tdStyle = (align) => ({
    padding: '10px 12px',
    textAlign: align || 'right',
    borderBottom: '1px solid rgba(30,45,79,0.8)',
    verticalAlign: 'middle',
  });

  return (
    <div style={card}>
      <div style={{ marginBottom:'16px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div>
          <div style={{ fontFamily:'Space Grotesk', fontWeight:600, fontSize:'16px', color:'#F0F4FF' }}>
            Top Source IPs {compact ? '(Preview)' : ''}
          </div>
          <div style={{ fontFamily:'Inter', fontSize:'12px', color:'#5B7BA8', marginTop:'4px' }}>
            IP addresses sending email as your domain
          </div>
        </div>
      </div>

      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle('ip')} onClick={() => setSortBy('ip')}>Source IP</th>
              {!compact && <th style={{ ...thStyle('country'), textAlign:'left' }}>Location</th>}
              {!compact && <th style={{ ...thStyle('isp'), textAlign:'left' }}>ISP / Org</th>}
              <th style={thStyle('total')} onClick={() => setSortBy('total')}>Total ↕</th>
              <th style={thStyle('pass')} onClick={() => setSortBy('pass')}>Pass</th>
              <th style={thStyle('fail')} onClick={() => setSortBy('fail')}>Fail</th>
              <th style={{ ...thStyle('rate'), minWidth:'140px' }} onClick={() => setSortBy('rate')}>Pass Rate</th>
              {!compact && <th style={thStyle('domains')}>Domains</th>}
            </tr>
          </thead>
          <tbody>
            {sorted.map((src) => {
              const rate = src.total > 0 ? Math.round((src.pass / src.total) * 100) : 0;
              const failColor = src.fail > 0 ? '#E8415A' : '#5B7BA8';
              const geo = g[src.ip];
              return (
                <tr key={src.ip} style={{ transition:'background 0.1s' }}
                  onMouseEnter={e => e.currentTarget.style.background='rgba(0,194,168,0.04)'}
                  onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                  <td style={{ ...tdStyle('left') }}>
                    <span style={{ fontFamily:'JetBrains Mono', fontSize:'13px', color:'#F0F4FF' }}>{src.ip}</span>
                  </td>
                  {!compact && (
                    <td style={{ ...tdStyle('left') }}>
                      {geo ? (
                        <span style={{ fontFamily:'Inter', fontSize:'12px', color:'#7B9CC8' }}>
                          {countryFlag(geo.countryCode)}&nbsp;{geo.city ? `${geo.city}, ` : ''}{geo.country || '—'}
                        </span>
                      ) : <span style={{ color:'#3A4F6E', fontSize:'12px' }}>—</span>}
                    </td>
                  )}
                  {!compact && (
                    <td style={{ ...tdStyle('left'), maxWidth:'180px' }}>
                      {geo ? (
                        <div>
                          <div style={{ fontFamily:'Inter', fontSize:'12px', color:'#7B9CC8', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{geo.isp || '—'}</div>
                          <div style={{ fontFamily:'JetBrains Mono', fontSize:'10px', color:'#3A4F6E', marginTop:'2px' }}>{geo.as || ''}</div>
                        </div>
                      ) : <span style={{ color:'#3A4F6E', fontSize:'12px' }}>—</span>}
                    </td>
                  )}
                  <td style={tdStyle()}>
                    <span style={{ fontFamily:'JetBrains Mono', fontSize:'13px', color:'#7B9CC8' }}>{src.total.toLocaleString()}</span>
                  </td>
                  <td style={tdStyle()}>
                    <span style={{ fontFamily:'JetBrains Mono', fontSize:'13px', color:'#00C2A8' }}>{src.pass.toLocaleString()}</span>
                  </td>
                  <td style={tdStyle()}>
                    <span style={{ fontFamily:'JetBrains Mono', fontSize:'13px', color:failColor }}>{src.fail.toLocaleString()}</span>
                  </td>
                  <td style={{ ...tdStyle(), minWidth:'140px' }}>
                    <PassBadge rate={rate} />
                  </td>
                  {!compact && (
                    <td style={tdStyle()}>
                      <div style={{ display:'flex', gap:'4px', flexWrap:'wrap', justifyContent:'flex-end' }}>
                        {(src.domains || []).map(d => (
                          <span key={d} style={{ fontFamily:'JetBrains Mono', fontSize:'11px', color:'#5B7BA8', background:'rgba(30,45,79,0.8)', padding:'2px 6px', borderRadius:'4px' }}>
                            {d}
                          </span>
                        ))}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
